import moveLane from './moveLane.json'
import voteCard from './voteCard.json'
import createLane from './createLane.json'
import deleteLane from './deleteLane.json'
import createCard from './createCard.json'
import deleteCard from './deleteCard.json'
import createEvent from './createEvent.json'
import deleteEvent from './deleteEvent.json'
import createEntity from './createEntity.json'
import updateEntity from './updateEntity.json'
import deleteEntity from './deleteEntity.json'
import updateLaneName from './updateLaneName.json'
import createWriteModel from './createWriteModel.json'
import updateWorkflowName from './updateWorkflowName.json'
import updateCardDescription from './updateCardDescription.json'
import updateEventDescription from './updateEventDescription.json'
import CardApi from '@/api/cardApi'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'

const tools = [
  updateWorkflowName,
  createEvent,
  updateEventDescription,
  deleteEvent,
  createLane,
  updateLaneName,
  deleteLane,
  moveLane,
  createCard,
  deleteCard,
  updateCardDescription,
  voteCard,
  createEntity,
  updateEntity,
  deleteEntity,
  createWriteModel
]

const functionType = {
  UPDATE_WORKFLOW_NAME: 'updateWorkflowName',
  CREATE_EVENT: 'createEvent',
  UPDATE_EVENT_DESCRIPTION: 'updateEventDescription',
  DELETE_EVENT: 'deleteEvent',
  CREATE_LANE: 'createLane',
  UPDATE_LANE_NAME: 'updateLaneName',
  DELETE_LANE: 'deleteLane',
  MOVE_LANE: 'moveLane',
  CREATE_CARD: 'createCard',
  DELETE_CARD: 'deleteCard',
  UPDATE_CARD_DESCRIPTION: 'updateCardDescription',
  VOTE_CARD: 'voteCard',
  CREATE_ENTITY: 'createEntity',
  UPDATE_ENTITY: 'updateEntity',
  DELETE_ENTITY: 'deleteEntity',
  CREATE_WRITE_MODEL: 'createWriteModel'
}

const functions = {
  [functionType.UPDATE_WORKFLOW_NAME]: async (args, { workflow }) => {
    const { name } = args

    return await workflowApi.updateWorkflowName({
      ...workflow,
      name,
      updateNote: {
        action: 'changed workflow name to',
        target: name
      }
    })
  },
  [functionType.CREATE_EVENT]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { description, laneName, parentEventName } = args

    const parentEvent = parentEventName === 'start' ? null : (workflow.eventsJson ?? []).find((event) => event.description === parentEventName)

    return await workflowApi.addEventToWorkflow({
      type: 'bpmn:Task',
      parents: parentEvent ? [parentEvent.id] : ['start'],
      description
    }, { laneName }, svgLanes, svgGraph, svgGroups, workflow)
  },
  [functionType.UPDATE_EVENT_DESCRIPTION]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { eventId, description } = args

    if (!svgGraph[eventId]) {
      return
    }

    const updatedSvgGraph = safeStructuredClone(svgGraph)
    updatedSvgGraph[eventId].name = description
    return await workflowApi.updateWorkflow({
      svgGraph: updatedSvgGraph,
      svgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: svgGraph[eventId].type === 'bpmn:Task' ? 'updated event text to' : 'updated decision text to',
        target: description
      }
    })
  },
  [functionType.DELETE_EVENT]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { eventId } = args
    return await workflowApi.deleteEventFromWorkflow(eventId, svgGraph, svgLanes, svgGroups, workflow)
  },
  [functionType.CREATE_LANE]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { name } = args
    return await diagramHelpers.methods.addLane(svgGraph, svgLanes, svgGroups, workflow, name)
  },
  [functionType.UPDATE_LANE_NAME]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { laneId, name } = args

    if (!svgLanes[laneId]) {
      return
    }

    svgLanes[laneId].name = name
    return await workflowApi.updateWorkflow({
      svgLanes,
      svgGraph,
      svgGroups,
      workflow,
      updateNote: {
        action: 'updated lane name to',
        target: name
      }
    })
  },
  [functionType.DELETE_LANE]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { laneId } = args

    if (!svgLanes[laneId]) {
      return
    }

    const name = svgLanes[laneId].name
    delete svgLanes[laneId]
    return await workflowApi.updateWorkflow({
      svgLanes,
      svgGraph,
      svgGroups,
      workflow,
      updateNote: {
        action: 'deleted lane',
        target: name
      }
    })
  },
  [functionType.MOVE_LANE]: async (args, { workflow, svgGraph, svgLanes, svgGroups }) => {
    const { laneId, targetIndex } = args

    const laneEntries = Object.entries(svgLanes)
    const laneIndex = laneEntries.findIndex(([id]) => id === laneId)

    if (laneIndex === -1 || targetIndex < 0 || targetIndex >= laneEntries.length) {
      console.error('Invalid lane move operation')
      return
    }

    const [movedLane] = laneEntries.splice(laneIndex, 1)
    laneEntries.splice(targetIndex, 0, movedLane)
    const newSvgLanes = Object.fromEntries(laneEntries)

    return await workflowApi.updateWorkflow({
      svgGraph,
      svgLanes: newSvgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: 'moved lane',
        target: `${movedLane[1].name} to position ${targetIndex + 1}`
      }
    })
  },
  [functionType.CREATE_CARD]: async (args, { workflow }) => {
    const { eventId, cardTypeId, description } = args

    return await workflowApi.createRequirementJson(workflow.id, eventId, {
      cardTypeId,
      description
    })
  },
  [functionType.DELETE_CARD]: async (args, { workflow }) => {
    const { cardId } = args
    return await workflowApi.deleteRequirementJson(workflow.id, { requirementId: cardId })
  },
  [functionType.UPDATE_CARD_DESCRIPTION]: async (args, { workflow }) => {
    const { cardId, description } = args
    return await workflowApi.updateRequirementsJsonMutation(workflow.id, [{ requirementId: cardId, description: `<p>${description}</p>` }])
  },
  [functionType.VOTE_CARD]: async (args, { workflow }) => {
    const { cardId, voteType } = args
    return await CardApi.voteCard({
      workflowId: workflow.id,
      input: {
        requirementId: cardId,
        direction: voteType
      }
    })
  },
  [functionType.CREATE_ENTITY]: async (args, { workflow }) => {
    const { name, dataFields } = args

    const newEntity = {
      name,
      type: 'Dimension',
      dataFields: dataFields.map((item) => ({
        name: item.name,
        cardinality: item.cardinality || 'one-to-one',
        exampleData: item.exampleData || [],
        primaryKey: item.primaryKey,
        reference: item.reference,
        relatedEntityId: item.relatedEntityId || null,
        tags: item.isRequired ? ['REQUIRED'] : []
      }))
    }

    const updatedEntities = [...(workflow.dataModels || []), newEntity]

    return await workflowApi.updateDataModels({
      dataModelsOnWorkflow: updatedEntities,
      workflow
    })
  },
  [functionType.UPDATE_ENTITY]: async (args, { workflow }) => {
    const { id, name, dataFields } = args

    const updatedEntities = (workflow.dataModels || []).map((entity) => {
      if (entity.id === id) {
        return {
          ...entity,
          name: name || entity.name,
          dataFields: (dataFields || []).map((item) => ({
            ...entity.dataFields[item.index],
            name: item.name,
            cardinality: item.cardinality || 'one-to-one',
            exampleData: item.exampleData || [],
            primaryKey: item.primaryKey,
            reference: item.reference,
            relatedEntityId: item.relatedEntityId || null,
            tags: item.isRequired ? ['REQUIRED'] : []
          }))
        }
      }
      return entity
    })

    return await workflowApi.updateDataModels({
      dataModelsOnWorkflow: updatedEntities,
      workflow
    })
  },
  [functionType.DELETE_ENTITY]: async (args, { workflow }) => {
    const { entityId } = args

    const updatedEntities = (workflow.dataModels || []).filter((entity) => entity.id !== entityId)

    return await workflowApi.updateDataModels({
      dataModelsOnWorkflow: updatedEntities,
      workflow
    })
  },
  [functionType.CREATE_WRITE_MODEL]: async (args, { workflow, svgGraph }) => {
    const { eventId, name, dataFields } = args
    const event = svgGraph[eventId]

    if (!event) {
      return
    }

    const commandCard = cardHelpers.methods.getCommandCard(event)

    const writeModel = {
      name,
      type: 'Event Story',
      cardId: commandCard?.id,
      dataFields: dataFields.map((item) => ({
        name: item.name,
        cardinality: item.cardinality || 'one-to-one',
        relatedEntityId: item.relatedEntityId || null
      }))
    }

    const updatedDataModels = (event.dataModels || []).filter((item) => item.type !== 'Event Story')
    updatedDataModels.push(writeModel)

    await workflowApi.updateDataModels({
      eventId,
      dataModelsOnEvent: updatedDataModels,
      workflow
    })
  }
}

const callFunction = async (name, aiArguments, additionalArguments) => {
  if (functions[name]) {
    return await functions[name](aiArguments, additionalArguments)
  }
}

export default tools
export { functions, functionType, callFunction }
